import { pxToEm } from 'util/pxToEm';
import { formatDate } from 'util/formatDate';
import React, { ReactElement } from 'react';
import { Box, Typography } from '@mui/material';
import { ArrowForwardIos } from '@mui/icons-material';
import { Image, Link } from 'components/lib';
import { FollowOrganizationButton } from 'components/app/FollowOrganizationButton';
import { colorSkeletonBackground } from 'customColors';

// Loading skeleton component
export const LoadingSkeleton = (): ReactElement => (
  <Box>
    {Array(4).fill(undefined).map((_, index) => (
      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <Box sx={{ width: 80, height: 80, flexShrink: 0, backgroundColor: colorSkeletonBackground, borderRadius: 4 }} />
        <Box flex={1}>
          <Box sx={{ width: '60%', height: 16, backgroundColor: colorSkeletonBackground, borderRadius: 2, mb: 8 }} />
          <Box sx={{ width: '40%', height: 12, backgroundColor: colorSkeletonBackground, borderRadius: 2 }} />
        </Box>
      </Box>
    ))}
  </Box>
);

// Empty state component
interface EmptyStateProps {
  displayName: string;
  isFollowed: boolean;
  organizationId: string;
}

export const EmptyState = ({ displayName, isFollowed, organizationId }: EmptyStateProps): ReactElement => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 48, textAlign: 'center' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width="25" height="26" viewBox="0 0 25 26" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1.33333 9.128H20V5.12833C20 4.923 19.9146 4.73489 19.7437 4.564C19.5728 4.39311 19.3847 4.30767 19.1793 4.30767H2.154C1.94867 4.30767 1.76056 4.39311 1.58967 4.564C1.41878 4.73489 1.33333 4.923 1.33333 5.12833V9.128ZM2.154 24.3077C1.54022 24.3077 1.02778 24.1021 0.616667 23.691C0.205556 23.2799 0 22.7674 0 22.1537V5.12833C0 4.51455 0.205556 4.00211 0.616667 3.591C1.02778 3.17989 1.54022 2.97433 2.154 2.97433H4.51267V0H5.94867V2.97433H15.4873V0H16.8207V2.97433H19.1793C19.7931 2.97433 20.3056 3.17989 20.7167 3.591C21.1278 4.00211 21.3333 4.51455 21.3333 5.12833V12.1307C21.1162 12.0504 20.8966 11.9907 20.6743 11.9513C20.4521 11.912 20.2273 11.8761 20 11.8437V10.4617H1.33333V22.1537C1.33333 22.359 1.41878 22.5471 1.58967 22.718C1.76056 22.8889 1.94867 22.9743 2.154 22.9743H11.0923C11.1623 23.2239 11.2517 23.4556 11.3603 23.6693C11.4688 23.8829 11.582 24.0957 11.7 24.3077H2.154ZM19.1793 25.641C17.694 25.641 16.4338 25.1234 15.3987 24.0883C14.3636 23.0534 13.846 21.7932 13.846 20.3077C13.846 18.8221 14.3636 17.5619 15.3987 16.527C16.4338 15.4919 17.694 14.9743 19.1793 14.9743C20.6649 14.9743 21.9252 15.4919 22.9603 16.527C23.9952 17.5619 24.5127 18.8221 24.5127 20.3077C24.5127 21.7932 23.9952 23.0534 22.9603 24.0883C21.9252 25.1234 20.6649 25.641 19.1793 25.641ZM21.3873 23.2437L22.1153 22.5153L19.6923 20.0923V16.4617H18.6667V20.523L21.3873 23.2437Z" fill="#1C1B1F" />
      </svg>
    </Box>
    <Typography sx={{ fontSize: pxToEm(16), color: '#383838', mb: 24, fontFamily: 'Poppins', fontWeight: 500 }}>
      No upcoming events yet
    </Typography>
    <Typography sx={{ fontSize: pxToEm(16), color: '#383838', fontFamily: 'Poppins', fontWeight: 400, mb: 24 }}>
      Stay updated on events from {displayName}.
    </Typography>
    {!isFollowed && <FollowOrganizationButton organizationId={organizationId} />}
  </Box>
);

// Event item component
interface EventItemProps {
  event: {
    id: string;
    url: string;
    flyerImageUrl: string;
    name: string;
    date: string;
  };
  onClose: () => void;
}

const EventItem = ({ event, onClose }: EventItemProps): ReactElement => (
  <Link href={event.url} onClick={onClose} sx={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 16, cursor: 'pointer', backgroundColor: 'transparent', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.02)' } }}>
      <Box sx={{ width: 60, height: 80, minWidth: 60, flexShrink: 0, overflow: 'hidden', backgroundColor: '#f0f0f0', borderRadius: 4 }}>
        <Image src={event.flyerImageUrl} alt={event.name} width={60} height={80} style={{ objectFit: 'cover', width: '100%', height: '100%' }} />
      </Box>
      <Box flex={1} minWidth={0} display="flex" flexDirection="column" gap={2}>
        <Typography sx={{ fontWeight: 500, fontSize: pxToEm(16), lineHeight: 1.5, color: '#383838', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {event.name}
        </Typography>
        <Typography sx={{ fontWeight: 500, fontSize: pxToEm(12), lineHeight: 1.5, color: '#383838' }}>
          {formatDate(new Date(event.date), { includeDay: true, includeDate: true, includeYear: true })}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', color: '#383838', flexShrink: 0 }}>
        <ArrowForwardIos sx={{ fontSize: pxToEm(16) }} />
      </Box>
    </Box>
  </Link>
);

// Event list component
export interface EventListProps {
  events: Array<{
    id: string;
    url: string;
    flyerImageUrl: string;
    name: string;
    date: string;
  }>;
  onClose: () => void;
}

export const EventList = ({ events, onClose }: EventListProps): ReactElement => (
  <Box display="flex" flexDirection="column" gap={16} sx={{ maxHeight: 300, overflowY: 'auto' }}>
    <Typography sx={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: pxToEm(14), lineHeight: 1.5, color: '#383838' }}>
      Upcoming Events
    </Typography>
    {events.map(event => (
      <EventItem key={event.id} event={event} onClose={onClose} />
    ))}
  </Box>
);

// Events section container
export interface EventsSectionProps {
  isLoading: boolean;
  events: EventListProps['events'];
  displayName: string;
  isFollowed: boolean;
  organizationId: string;
  onClose: () => void;
}

export const EventsSection = ({
  isLoading,
  events,
  displayName,
  isFollowed,
  organizationId,
  onClose,
}: EventsSectionProps): ReactElement => {
  if (isLoading) return <LoadingSkeleton />;
  if (events.length === 0) return <EmptyState displayName={displayName} isFollowed={isFollowed} organizationId={organizationId} />;
  return <EventList events={events} onClose={onClose} />;
};
